import React, { useState } from 'react';
import { useAdminPath } from '../MarketerView/useScopedNavigate';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Popconfirm,
  message,
  Tooltip,
  Alert,
  Empty,
  Switch,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CrownOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetSegmentationFieldsQuery,
  useGetSegmentationFieldsLimitQuery,
  useCreateSegmentationFieldMutation,
  useUpdateSegmentationFieldMutation,
  useDeleteSegmentationFieldMutation,
  ISegmentationField,
  ICreateSegmentationFieldDto,
  ISegmentationFieldMetadata,
  IUpdateSegmentationFieldDto,
  SegmentationFieldType,
} from '../../services/nestjs-base-api';
import { useSegmentationTierAccess } from '../../hooks/useTierAccess';
import { TierUpgradePrompt } from '../TierUpgradePrompt';

const { Text, Paragraph } = Typography;
const { Option } = Select;

interface CustomerSegmentationProps {
  organisationId?: number;
}

const fieldTypeLabels: Record<SegmentationFieldType, string> = {
  text: 'Text',
  select: 'Select (Dropdown)',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / No',
  multi_select: 'Multi-Select (Tags)',
  currency: 'Currency',
};

const fieldTypeColors: Record<SegmentationFieldType, string> = {
  text: 'blue',
  select: 'green',
  number: 'purple',
  date: 'gold',
  boolean: 'cyan',
  multi_select: 'magenta',
  currency: 'orange',
};

const SUPPORTED_CURRENCY_CODES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'NZD', 'SEK', 'NOK',
  'DKK', 'SGD', 'HKD', 'INR', 'BRL', 'MXN', 'ZAR', 'AED',
];

/**
 * Customer Segmentation Management Component
 * Allows organisations to create and manage custom segmentation fields
 */
export const CustomerSegmentation: React.FC<CustomerSegmentationProps> = ({
  organisationId,
}) => {
  const { user } = useAuth();
  const adminPath = useAdminPath();
  const _orgId = organisationId || user?.organisation?.id;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<ISegmentationField | null>(null);
  const [form] = Form.useForm();

  // Tier access
  const { hasAccess, fieldsLimit, unlimited, isLoading: tierLoading } = useSegmentationTierAccess();

  // API queries
  const { data: fields, isLoading: fieldsLoading, refetch } = useGetSegmentationFieldsQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken }
  );

  const { data: limitInfo } = useGetSegmentationFieldsLimitQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken }
  );

  // API mutations
  const [createField, { isLoading: creating }] = useCreateSegmentationFieldMutation();
  const [updateField, { isLoading: updating }] = useUpdateSegmentationFieldMutation();
  const [deleteField, { isLoading: deleting }] = useDeleteSegmentationFieldMutation();

  const isLoading = fieldsLoading || tierLoading;
  const activeFields = fields?.filter(f => f.isActive) || [];
  const canAddMore = unlimited || activeFields.length < fieldsLimit;

  // Show upgrade prompt if no access
  if (!hasAccess && !tierLoading) {
    return (
      <TierUpgradePrompt
        featureName="Customer Segmentation"
        featureDescription="Create custom fields to segment your customers and analyze brand equity across different groups"
        requiredPlan="Professional"
      />
    );
  }

  const handleOpenModal = (field?: ISegmentationField) => {
    if (field) {
      setEditingField(field);
      form.setFieldsValue({
        fieldName: field.fieldName,
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
        options: field.options?.join('\n') || '',
        isActive: field.isActive,
        currencyCode: field.fieldMetadata?.currencyCode ?? 'USD',
        isPii: field.fieldMetadata?.isPii ?? false,
      });
    } else {
      setEditingField(null);
      form.resetFields();
      form.setFieldsValue({
        fieldType: 'text',
        isActive: true,
        currencyCode: 'USD',
        isPii: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingField(null);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      const fieldType: SegmentationFieldType = values.fieldType;
      const optionTypes: SegmentationFieldType[] = ['select', 'multi_select'];
      const optionsList: string[] | null =
        optionTypes.includes(fieldType) && values.options
          ? values.options
              .split('\n')
              .map((o: string) => o.trim())
              .filter((o: string) => o)
          : null;

      const fieldMetadata: ISegmentationFieldMetadata = {
        source: 'manual',
        isPii: values.isPii ?? false,
      };
      if (fieldType === 'currency' && values.currencyCode) {
        fieldMetadata.currencyCode = values.currencyCode;
      }

      const dto: ICreateSegmentationFieldDto | IUpdateSegmentationFieldDto = {
        fieldName: values.fieldName,
        fieldLabel: values.fieldLabel,
        fieldType,
        options: optionsList ?? undefined,
        fieldMetadata,
        isActive: values.isActive ?? true,
      };

      if (editingField) {
        await updateField({
          jwtToken: user?.jwtToken || '',
          fieldId: editingField.id,
          payload: dto as IUpdateSegmentationFieldDto,
        }).unwrap();
        message.success('Segmentation field updated successfully');
      } else {
        await createField({
          jwtToken: user?.jwtToken || '',
          payload: dto as ICreateSegmentationFieldDto,
        }).unwrap();
        message.success('Segmentation field created successfully');
      }

      handleCloseModal();
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to save segmentation field');
    }
  };

  const handleDelete = async (fieldId: number) => {
    try {
      await deleteField({
        jwtToken: user?.jwtToken || '',
        fieldId: fieldId,
      }).unwrap();
      message.success('Segmentation field deleted successfully');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Failed to delete segmentation field');
    }
  };

  const columns = [
    {
      title: 'Field Name',
      dataIndex: 'fieldLabel',
      key: 'fieldLabel',
      render: (text: string, record: ISegmentationField) => (
        <Space>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({record.fieldName})
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'fieldType',
      key: 'fieldType',
      render: (type: SegmentationFieldType) => (
        <Tag color={fieldTypeColors[type]}>{fieldTypeLabels[type]}</Tag>
      ),
    },
    {
      title: 'Options',
      dataIndex: 'options',
      key: 'options',
      render: (options: string[] | null, record: ISegmentationField) => {
        const optionTypes: SegmentationFieldType[] = ['select', 'multi_select'];
        if (!optionTypes.includes(record.fieldType) || !options || options.length === 0) {
          if (record.fieldType === 'currency' && record.fieldMetadata?.currencyCode) {
            return <Text type="secondary">{record.fieldMetadata.currencyCode}</Text>;
          }
          return <Text type="secondary">-</Text>;
        }
        return (
          <Tooltip title={options.join(', ')}>
            <Text>{options.length} options</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'PII',
      key: 'isPii',
      render: (_: any, record: ISegmentationField) =>
        record.fieldMetadata?.isPii ? (
          <Tag color="red">PII</Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ISegmentationField) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this segmentation field?"
            description="This will remove the field and all associated customer data."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes, delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <TagsOutlined />
            <span>Customer Segmentation</span>
          </Space>
        }
        extra={
          <Space>
            {!unlimited && (
              <Text type="secondary">
                {activeFields.length} / {fieldsLimit} fields used
              </Text>
            )}
            {unlimited && (
              <Tag color="green">Unlimited Fields</Tag>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              disabled={!canAddMore}
            >
              Add Field
            </Button>
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Create custom fields to segment your customers. These fields can be used to filter
          and analyze brand equity data across different customer groups.
        </Paragraph>

        {!canAddMore && !unlimited && (
          <Alert
            type="warning"
            message="Field Limit Reached"
            description={
              <Space direction="vertical">
                <Text>
                  You've reached the maximum of {fieldsLimit} segmentation field{fieldsLimit !== 1 ? 's' : ''} for your plan.
                </Text>
                <Button
                  type="link"
                  icon={<CrownOutlined />}
                  onClick={() => window.location.href = adminPath('/dashboard/organisation?tab=subscription')}
                  style={{ padding: 0 }}
                >
                  Upgrade your plan for more fields
                </Button>
              </Space>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={fields || []}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical">
                    <Text>No segmentation fields created yet</Text>
                    <Text type="secondary">
                      Create your first field to start segmenting customers
                    </Text>
                  </Space>
                }
              />
            ),
          }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} fields`,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingField ? 'Edit Segmentation Field' : 'Create Segmentation Field'}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="fieldLabel"
            label="Field Label"
            rules={[{ required: true, message: 'Please enter a field label' }]}
            tooltip="The display name shown to users"
          >
            <Input placeholder="e.g., Customer Type, Industry, Region" />
          </Form.Item>

          <Form.Item
            name="fieldName"
            label="Field Name"
            rules={[
              { required: true, message: 'Please enter a field name' },
              { pattern: /^[a-z][a-z0-9_]*$/, message: 'Must start with lowercase letter, only lowercase letters, numbers, and underscores' },
            ]}
            tooltip="Technical name used in the system (lowercase, no spaces)"
          >
            <Input placeholder="e.g., customer_type, industry, region" />
          </Form.Item>

          <Form.Item
            name="fieldType"
            label="Field Type"
            rules={[{ required: true, message: 'Please select a field type' }]}
          >
            <Select>
              <Option value="text">
                <Space>
                  <Tag color="blue">Text</Tag>
                  Free-form text input
                </Space>
              </Option>
              <Option value="select">
                <Space>
                  <Tag color="green">Select</Tag>
                  Dropdown with predefined options
                </Space>
              </Option>
              <Option value="number">
                <Space>
                  <Tag color="purple">Number</Tag>
                  Numeric value
                </Space>
              </Option>
              <Option value="date">
                <Space>
                  <Tag color="gold">Date</Tag>
                  Calendar date (YYYY-MM-DD)
                </Space>
              </Option>
              <Option value="boolean">
                <Space>
                  <Tag color="cyan">Yes / No</Tag>
                  Boolean true / false
                </Space>
              </Option>
              <Option value="multi_select">
                <Space>
                  <Tag color="magenta">Multi-Select</Tag>
                  Tags / multiple choices
                </Space>
              </Option>
              <Option value="currency">
                <Space>
                  <Tag color="orange">Currency</Tag>
                  Money amount with currency code
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.fieldType !== currentValues.fieldType
            }
          >
            {({ getFieldValue }) => {
              const ft = getFieldValue('fieldType') as SegmentationFieldType;
              if (ft === 'select' || ft === 'multi_select') {
                return (
                  <Form.Item
                    name="options"
                    label="Options"
                    rules={[{ required: true, message: 'Please enter at least one option' }]}
                    tooltip="Enter one option per line"
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="Enterprise&#10;Mid-Market&#10;Small Business&#10;Startup"
                    />
                  </Form.Item>
                );
              }
              if (ft === 'currency') {
                return (
                  <Form.Item
                    name="currencyCode"
                    label="Currency"
                    rules={[{ required: true, message: 'Please pick a currency code' }]}
                    tooltip="ISO-4217 currency code"
                  >
                    <Select showSearch>
                      {SUPPORTED_CURRENCY_CODES.map((c) => (
                        <Option key={c} value={c}>{c}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item
            name="isPii"
            label="Personally Identifiable Information (PII)"
            valuePropName="checked"
            tooltip="When ON, this field is redacted in logs/exports and cleared from snapshots when a customer is deleted (GDPR)."
          >
            <Switch checkedChildren="PII" unCheckedChildren="Non-PII" />
          </Form.Item>

          {editingField && (
            <Form.Item
              name="isActive"
              label="Status"
              valuePropName="checked"
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          )}

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={creating || updating}
              >
                {editingField ? 'Update Field' : 'Create Field'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerSegmentation;
